import { localisateurAffiche } from '../domaine/citations';
// Écran « Documents » : les livrables de l'architecte pré-remplis par Ekera depuis les gabarits Word de l'entreprise
// (DAT, dossier d'homologation, HLD, EBIOS RM). Décision du fondateur du 22 octobre 2026 : la consultation se fait ICI,
// sans site tiers ni site public ; téléchargement Word et PDF ; publication vers l'outil documentaire de l'entreprise
// (SharePoint d'abord, Confluence ensuite) seulement si elle est configurée ET qu'un collecteur du site sait publier.
//
// Règles visibles à l'écran : chaque valeur montre sa source (au survol et dans la marge) ; ce qu'aucune source ne règle
// est « à compléter » et mis en évidence ; aucun texte n'est rédigé par un modèle ; rien n'est publié sans la validation
// d'une personne, sur le contenu exact qu'elle a lu (empreinte).
//
// Décisions du fondateur du 22 octobre 2026 : la validation du contenu exige le geste WebAuthn, comme une écriture
// (plan interne gardé par l'API, défi, geste, validation) ; la publication passe par le collecteur, comme GLPI : le
// collecteur calcule le plan (compte de l'outil gardé chez l'entreprise), l'API le garde, la personne le valide par un
// geste, puis le collecteur l'exécute. Défi, assertion et validation ne vivent qu'en mémoire, le temps de les remettre.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ErreurApi, delaiLisible, expliquerErreur, nouvelleCle } from '../api/client';
import type { ChampVue, ResultatPublication, ResumeLivrable, VueLivrable } from '../api/types';
import { Attendre, Etiquette } from '../composants/communs';
import { RappelAssurance } from '../composants/RappelAssurance';
import { RapportDiagnostic } from '../composants/RapportDiagnostic';
import { useEkera } from '../contexte';
import {
  citationsDuChamp,
  LIBELLE_CIBLE,
  LIBELLE_ETAT,
  LIBELLE_ORIGINE,
  libelleType,
  resumeSource,
  TON_ETAT,
  trierLivrables,
} from '../domaine/documents';
import { dateFr } from '../domaine/format';
import { telecharger } from '../domaine/telechargement';
import { empreintePlan, memePlan, type ReponsePlan } from '../domaine/validations';
import { ErreurGeste, gesteAssertion, indisponibiliteNavigateur } from '../domaine/webauthn';
import { lienVers } from '../routeur';

/**
 * Message d'une erreur des routes des documents. Le plafond de débit (429, Retry-After) et le corps trop gros (413)
 * ont ici leur propre texte : le code « debit_depasse » est aussi celui de la console, dont le message parle de
 * questions.
 */
export function expliquerErreurDocuments(e: unknown): string {
  if (e instanceof ErreurApi && e.statut === 429)
    return e.reessayerApres
      ? `Trop de demandes, réessayez dans ${delaiLisible(e.reessayerApres)}. Rien n’a été fait.`
      : 'Trop de demandes, réessayez dans quelques instants. Rien n’a été fait.';
  if (e instanceof ErreurApi && e.statut === 413)
    return 'La demande envoyée est trop grosse pour l’API : rien n’a été fait.';
  return expliquerErreur(e);
}

function Erreur({ erreur, titre }: { erreur: unknown; titre?: string }) {
  return (
    <div className="encadre erreur" role="alert">
      <p>
        <b>{titre ?? 'L’API a refusé la demande.'}</b>
      </p>
      <p>{expliquerErreurDocuments(erreur)}</p>
    </div>
  );
}

export function EcranDocuments({ params }: { params: URLSearchParams }) {
  const lid = params.get('livrable');
  return lid ? <Document lid={lid} /> : <ListeDocuments />;
}

// ---------------------------------------------------------------- liste

function ListeDocuments() {
  const { client, cid, demonstration } = useEkera();
  const liste = useQuery({ queryKey: ['carte', cid, 'livrables'], queryFn: () => client.livrables(cid) });
  return (
    <div className="pile">
      <p className="sous-titre">
        Ekera remplit les gabarits Word de votre entreprise à partir de la carte : chaque valeur cite sa source, chaque chiffre
        vient du moteur, et ce qu’aucune source ne règle reste « à compléter ». Vous les consultez ici, les validez, puis les
        téléchargez en Word ou en PDF.
      </p>
      <RapportDiagnostic />
      {demonstration && <RappelAssurance />}
      <Attendre etats={[liste]} quoi="des documents">
        {() =>
          liste.data!.length === 0 ? (
            <p className="encadre info">Aucun document n’est encore préparé pour cette carte.</p>
          ) : (
            <section className="panneau" aria-labelledby="t-docs">
              <div className="panneau-tete">
                <h2 id="t-docs">Livrables pré-remplis</h2>
                <span className="indice">DAT d’abord, puis dossier d’homologation ; HLD et EBIOS RM ensuite</span>
              </div>
              <div className="tableau-defile">
                <table>
                  <caption className="sr">Livrables de la carte</caption>
                  <thead>
                    <tr>
                      <th scope="col">Document</th>
                      <th scope="col">Gabarit</th>
                      <th scope="col">Rempli</th>
                      <th scope="col" style={{ textAlign: 'right' }}>
                        À compléter
                      </th>
                      <th scope="col">État</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trierLivrables(liste.data!).map((l) => (
                      <LigneDocument key={l.id} l={l} />
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )
        }
      </Attendre>
    </div>
  );
}

function LigneDocument({ l }: { l: ResumeLivrable }) {
  const type = libelleType(l.type_livrable);
  return (
    <tr>
      <td>
        <a href={lienVers('documents', { livrable: l.id })} className="nom-doc">
          <span className="etiquette neutre">{type.court}</span> {l.titre}
        </a>
        <small className="note" style={{ display: 'block' }}>
          Version {l.version} du {dateFr(l.date_document)}
          {l.citations_verifiees ? ' · citations vérifiées mot pour mot' : ' · citations non revérifiées'}
        </small>
      </td>
      <td className="note">{l.gabarit}</td>
      <td>
        <Remplissage pourcentage={l.pourcentage_rempli} remplis={l.remplis} champs={l.champs} />
      </td>
      <td className="num">{l.a_completer}</td>
      <td>
        <Etiquette ton={TON_ETAT[l.etat]}>{LIBELLE_ETAT[l.etat]}</Etiquette>
      </td>
    </tr>
  );
}

function Remplissage({ pourcentage, remplis, champs }: { pourcentage: number; remplis: number; champs: number }) {
  return (
    <span className="ligne" style={{ gap: 8, flexWrap: 'nowrap' }}>
      <span className="jauge large" role="img" aria-label={`${pourcentage} % des champs remplis (${remplis} sur ${champs})`}>
        <i style={{ width: `${pourcentage}%` }} />
      </span>
      <span className="num">{pourcentage} %</span>
      <span className="note">
        {remplis}/{champs}
      </span>
    </span>
  );
}

// ---------------------------------------------------------------- un document

function Document({ lid }: { lid: string }) {
  const { client, cid } = useEkera();
  const q = useQuery({ queryKey: ['carte', cid, 'livrable', lid], queryFn: () => client.livrable(cid, lid) });
  const [choisi, setChoisi] = useState<string | null>(null);
  return (
    <div className="pile">
      <p>
        <a href={lienVers('documents')}>← Tous les documents</a>
      </p>
      <Attendre etats={[q]} quoi="du document">
        {() => {
          const { vue, etag } = q.data!;
          const champs = vue.sections.flatMap((s) => s.champs);
          const actif = champs.find((c) => c.id === choisi) ?? null;
          return (
            <>
              <EnTeteDocument vue={vue} etag={etag} />
              {vue.liste_a_completer.length > 0 && (
                <details className="encadre attention" open={vue.liste_a_completer.length <= 6}>
                  <summary>
                    <b>
                      {vue.liste_a_completer.length} champ{vue.liste_a_completer.length > 1 ? 's' : ''} à compléter
                    </b>{' '}
                    : aucune source ne les règle, Ekera ne les invente pas.
                  </summary>
                  <ul className="liste-simple">
                    {vue.liste_a_completer.map((a) => (
                      <li key={a.champ}>
                        <button type="button" className="lien-btn" onClick={() => aller(a.champ, setChoisi)}>
                          {a.libelle}
                        </button>{' '}
                        <span className="note">
                          ({a.section}) — {a.raison_libelle ?? a.detail ?? 'aucune source'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              <div className="doc-zone">
                <article className="doc-page" aria-label={vue.titre}>
                  {vue.sections.map((s) => (
                    <section key={s.id} className="doc-section" aria-labelledby={`s-${s.id}`}>
                      <h2 id={`s-${s.id}`} className={s.niveau > 1 ? 'doc-h3' : undefined}>
                        {s.titre}
                        {s.relue && (
                          <span className="etiquette ok" style={{ marginLeft: 8, verticalAlign: 'middle' }}>
                            Relue
                          </span>
                        )}
                      </h2>
                      {s.champs.length === 0 && <p className="note">Section du gabarit sans champ à remplir.</p>}
                      {s.champs.map((c) => (
                        <Champ key={c.id} c={c} actif={c.id === choisi} surChoisir={() => setChoisi(c.id)} />
                      ))}
                    </section>
                  ))}
                </article>
                <aside className="doc-marge" aria-label="Sources du champ choisi">
                  <MargeSources c={actif} />
                </aside>
              </div>
            </>
          );
        }}
      </Attendre>
    </div>
  );
}

function aller(champ: string, choisir: (id: string) => void) {
  choisir(champ);
  const el = document.getElementById(`c-${champ}`);
  el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  el?.focus?.();
}

function EnTeteDocument({ vue, etag }: { vue: VueLivrable; etag: string }) {
  const type = libelleType(vue.type_livrable);
  return (
    <section className="panneau" aria-labelledby="t-doc">
      <div className="panneau-tete">
        <div className="pile" style={{ gap: 4 }}>
          <span className="ligne" style={{ gap: 8 }}>
            <span className="etiquette neutre">{type.court}</span>
            <Etiquette ton={TON_ETAT[vue.etat]}>{LIBELLE_ETAT[vue.etat]}</Etiquette>
          </span>
          <h2 id="t-doc">{vue.titre}</h2>
          <span className="note">
            {type.long} · gabarit {vue.gabarit} · version {vue.version} du {dateFr(vue.date_document)} · empreinte{' '}
            <span className="num">{vue.empreinte_contenu.slice(0, 12)}</span>
            {vue.correspondance_validee_par ? ` · correspondance du gabarit validée par ${vue.correspondance_validee_par}` : ''}
          </span>
        </div>
        <Remplissage pourcentage={vue.pourcentage_rempli} remplis={vue.remplis} champs={vue.champs} />
      </div>
      <p className="note">
        {vue.citations_verifiees
          ? 'Chaque citation a été retrouvée mot pour mot dans sa pièce au pré-remplissage. Survolez ou choisissez une valeur pour voir sa source.'
          : 'Les citations de ce document n’ont pas été revérifiées mot pour mot : il ne peut pas être publié.'}
      </p>
      <Actions vue={vue} etag={etag} />
      <Validations vue={vue} />
    </section>
  );
}

function Validations({ vue }: { vue: VueLivrable }) {
  if (!vue.validations?.length && !vue.publications?.length) return null;
  return (
    <ul className="liste-points">
      {(vue.validations ?? []).map((v) => (
        <li key={v.jeton ?? v.le} className={v.caduque ? undefined : 'ok'}>
          Validé par <b>{v.par}</b> le {dateFr(v.le)} (version {v.version})
          {v.commentaire ? ` — « ${v.commentaire} »` : ''}
          {v.caduque && <small>Le contenu a changé depuis : cette validation ne vaut plus.</small>}
        </li>
      ))}
      {(vue.publications ?? []).map((p) => (
        <li key={`${p.cible}-${p.le}`} className={p.etat === 'compensee' ? undefined : 'ok'}>
          {LIBELLE_ETAT[p.etat ?? 'publie']} dans {LIBELLE_CIBLE[p.cible] ?? p.cible} le {dateFr(p.le)}, validé par <b>{p.par}</b>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------- actions : télécharger, valider, publier

function Actions({ vue, etag }: { vue: VueLivrable; etag: string }) {
  const { client, cid, ecritureAutorisee, motifLectureSeule } = useEkera();
  const [telechargement, setTelechargement] = useState<{ occupe: string | null; erreur: unknown }>({ occupe: null, erreur: null });
  const [panneau, setPanneau] = useState<'valider' | 'publier' | null>(null);

  async function prendre(format: 'word' | 'pdf') {
    setTelechargement({ occupe: format, erreur: null });
    try {
      const b = await client.telechargerLivrable(cid, vue.id, format);
      telecharger(b, `${nomFichier(vue.titre)}.${format === 'word' ? 'docx' : 'pdf'}`);
      setTelechargement({ occupe: null, erreur: null });
    } catch (e) {
      setTelechargement({ occupe: null, erreur: e });
    }
  }
  const pub = vue.publication;
  const cible = pub.cible ? (LIBELLE_CIBLE[pub.cible] ?? pub.cible) : null;
  return (
    <div className="pile" style={{ gap: 10 }}>
      <div className="ligne">
        <button type="button" className="btn" onClick={() => void prendre('word')} disabled={!!telechargement.occupe}>
          {telechargement.occupe === 'word' ? 'Préparation…' : 'Télécharger en Word'}
        </button>
        <button type="button" className="btn" onClick={() => void prendre('pdf')} disabled={!!telechargement.occupe}>
          {telechargement.occupe === 'pdf' ? 'Préparation…' : 'Télécharger en PDF'}
        </button>
        {ecritureAutorisee && vue.etat === 'a_valider' && (
          <button
            type="button"
            className="btn principal"
            disabled={!vue.validation.possible}
            title={vue.validation.raison ?? undefined}
            onClick={() => setPanneau(panneau === 'valider' ? null : 'valider')}
            aria-expanded={panneau === 'valider'}
          >
            Valider ce document
          </button>
        )}
        {cible ? (
          <button
            type="button"
            className="btn sombre"
            disabled={!ecritureAutorisee || !pub.possible}
            title={!ecritureAutorisee ? motifLectureSeule : (pub.raison ?? undefined)}
            onClick={() => setPanneau(panneau === 'publier' ? null : 'publier')}
            aria-expanded={panneau === 'publier'}
          >
            Publier vers {cible}
          </button>
        ) : (
          <button type="button" className="btn" disabled title={pub.raison ?? undefined}>
            Publier vers l’outil de l’entreprise
          </button>
        )}
      </div>
      {ecritureAutorisee && vue.etat === 'a_valider' && !vue.validation.possible && vue.validation.raison && (
        <p className="note" data-testid="raison-validation">
          {vue.validation.raison} {vue.validation.raison.includes('Mes clés') && <a href={lienVers('cles')}>Aller à « Mes clés »</a>}
        </p>
      )}
      {!cible && <p className="note">{pub.raison}</p>}
      {cible && !pub.possible && pub.raison && <p className="note">{pub.raison}</p>}
      {!ecritureAutorisee && <p className="note">{motifLectureSeule}</p>}
      {telechargement.erreur ? <Erreur erreur={telechargement.erreur} titre="Téléchargement refusé." /> : null}
      {panneau === 'valider' && <PanneauValidation vue={vue} etag={etag} fermer={() => setPanneau(null)} />}
      {panneau === 'publier' && cible && <PanneauPublication vue={vue} cible={cible} destination={pub.destination} fermer={() => setPanneau(null)} />}
    </div>
  );
}

function nomFichier(titre: string): string {
  return titre.replace(/[\\/:*?"<>|#%\u0000-\u001f]+/g, '-').replace(/\s+/g, ' ').trim() || 'document';
}

/** Après ces erreurs, rien n'est signé et le plan reste validable : un nouveau clic redemande un défi. */
const CODES_REESSAYABLES = new Set(['geste_refuse', 'defi_expire', 'defis_trop_nombreux', 'defis_session_trop_nombreux', 'defis_compte_trop_nombreux']);
function reessayable(e: unknown): boolean {
  return e instanceof ErreurGeste || (e instanceof ErreurApi && CODES_REESSAYABLES.has(e.code));
}

type Envoi = false | 'plan' | 'defi' | 'geste' | 'signature' | 'publication';
const LIBELLE_ENVOI: Record<Exclude<Envoi, false>, string> = {
  plan: 'Calcul du plan…',
  defi: 'Demande du défi…',
  geste: 'Touchez votre clé de sécurité…',
  signature: 'Signature…',
  publication: 'Publication par le collecteur…',
};

function PanneauValidation({ vue, etag, fermer }: { vue: VueLivrable; etag: string; fermer: () => void }) {
  const { client, cid, moi } = useEkera();
  const qc = useQueryClient();
  const [commentaire, setCommentaire] = useState('');
  const [cle] = useState(nouvelleCle);
  // le plan gardé est réutilisé tant qu'il reste validable (un geste raté redemande seulement un défi)
  const [plan, setPlan] = useState<ReponsePlan | null>(null);
  const [envoi, setEnvoi] = useState<Envoi>(false);
  const [erreur, setErreur] = useState<unknown>(null);
  const navigateur = indisponibiliteNavigateur();

  async function valider() {
    setErreur(null);
    try {
      let recu = plan;
      if (!recu) {
        setEnvoi('plan');
        recu = await client.planValidationLivrable(cid, vue.id, vue.empreinte_contenu);
        // le plan gardé doit porter le contenu affiché ici, et son empreinte être celle que l'API annonce
        const contenu = (recu.plan.demande.contenu ?? {}) as Record<string, unknown>;
        if (contenu.empreinte_contenu !== vue.empreinte_contenu || (await empreintePlan(recu.plan)) !== recu.empreinte)
          throw new Error('Le plan rendu par l’API ne porte pas le contenu affiché : rien n’a été validé.');
        setPlan(recu);
      }
      setEnvoi('defi');
      const defi = await client.defiPlan(cid, recu.plan_id);
      setEnvoi('geste');
      const assertion = await gesteAssertion(defi.options);
      setEnvoi('signature');
      await client.validerLivrable(cid, vue.id, {
        empreinte: vue.empreinte_contenu,
        commentaire,
        etag,
        cle,
        plan_id: recu.plan_id,
        empreintePlan: recu.empreinte,
        defi_id: defi.defi_id,
        assertion,
      });
      await qc.invalidateQueries({ queryKey: ['carte', cid] });
      fermer();
    } catch (e) {
      setErreur(e);
      if (!reessayable(e)) setPlan(null);
    } finally {
      setEnvoi(false);
    }
  }
  return (
    <div className="encadre info pile" style={{ gap: 10 }} role="group" aria-label="Valider ce document">
      <p>
        Vous validez, à votre nom ({moi.nom ?? moi.sujet}), le contenu exact de la version {vue.version} tel qu’affiché ici
        (empreinte <span className="num">{vue.empreinte_contenu.slice(0, 12)}</span>), section par section, par un geste sur
        votre clé de sécurité. L’API signe cette validation ; si le contenu change, elle ne vaut plus.
      </p>
      {vue.a_completer > 0 && (
        <p>
          <b>
            {vue.a_completer} champ{vue.a_completer > 1 ? 's restent' : ' reste'} à compléter
          </b>{' '}
          : ils resteront marqués « à compléter » dans le document téléchargé.
        </p>
      )}
      <label className="champ">
        Commentaire (facultatif)
        <textarea value={commentaire} maxLength={2000} onChange={(e) => setCommentaire(e.target.value)} />
      </label>
      {navigateur && <p className="encadre attention">{navigateur}</p>}
      <div className="ligne">
        <button type="button" className="btn principal" onClick={() => void valider()} disabled={!!envoi || !!navigateur}>
          {envoi ? LIBELLE_ENVOI[envoi] : 'Valider à mon nom par un geste'}
        </button>
        <button type="button" className="btn" onClick={fermer} disabled={!!envoi}>
          Annuler
        </button>
      </div>
      {erreur ? <Erreur erreur={erreur} titre="Validation refusée." /> : null}
    </div>
  );
}

function PanneauPublication({
  vue,
  cible,
  destination,
  fermer,
}: {
  vue: VueLivrable;
  cible: string;
  destination?: string | null;
  fermer: () => void;
}) {
  const { client, cid } = useEkera();
  const qc = useQueryClient();
  const [cle] = useState(nouvelleCle);
  const [plan, setPlan] = useState<ReponsePlan | null>(null);
  const [envoi, setEnvoi] = useState<Envoi>(false);
  const [erreur, setErreur] = useState<unknown>(null);
  const [resultat, setResultat] = useState<ResultatPublication | null>(null);
  const navigateur = indisponibiliteNavigateur();
  // Circuit de validation (CV-3) : la publication est soumise au circuit du client ; elle ne s'exécute qu'avec
  // l'attestation de fin de circuit, sur le contenu actuel.
  const circuit = useQuery({ queryKey: ['circuit-livrable', cid, vue.id], queryFn: () => client.etatCircuitLivrable(cid, vue.id), retry: false });
  const [soumission, setSoumission] = useState(false);
  const etat = circuit.data;
  const attestation = etat?.exige && etat.etat === 'approuvee' && etat.sur_le_contenu_actuel ? etat.attestation ?? null : null;
  const enCours = !!etat?.exige && etat.soumis && etat.sur_le_contenu_actuel && etat.etat === 'en_cours';
  const aSoumettre = !!etat?.exige && !attestation && !enCours;
  async function soumettre() {
    setErreur(null);
    setSoumission(true);
    try {
      await client.soumettrePublication(cid, vue.id);
      await qc.invalidateQueries({ queryKey: ['circuit-livrable', cid, vue.id] });
    } catch (e) {
      setErreur(e);
    } finally {
      setSoumission(false);
    }
  }

  /** Premier temps : le collecteur du site calcule le plan (rien n'est écrit), l'API le garde pour vous. */
  async function demanderPlan() {
    setErreur(null);
    setEnvoi('plan');
    try {
      const recu = await client.planPublication(cid, vue.id);
      if ((await empreintePlan(recu.plan)) !== recu.empreinte)
        throw new Error('L’empreinte annoncée par l’API n’est pas celle du plan reçu : rien ne sera publié.');
      setPlan(recu);
    } catch (e) {
      setErreur(e);
    } finally {
      setEnvoi(false);
    }
  }

  /** Second temps : geste sur le défi du plan gardé, validation signée, puis exécution par le collecteur. */
  async function publier(recu: ReponsePlan) {
    setErreur(null);
    try {
      setEnvoi('defi');
      const defi = await client.defiPlan(cid, recu.plan_id);
      setEnvoi('geste');
      const assertion = await gesteAssertion(defi.options);
      setEnvoi('signature');
      // La validation signée n'existe qu'ici, en mémoire, le temps de la remettre à l'API.
      const v = await client.valider(
        cid,
        { plan_id: recu.plan_id, empreinte: recu.empreinte, defi_id: defi.defi_id, assertion, attestation_id: attestation },
        `Publication de « ${vue.titre} » vers ${cible}`,
      );
      if (!memePlan(v.plan, recu.plan)) throw new Error('L’API a rendu un autre plan que celui que vous avez lu : rien ne sera publié.');
      setEnvoi('publication');
      setResultat(await client.publierLivrable(cid, vue.id, v.plan, v.validation, cle, (v.attestation as Record<string, unknown> | null | undefined) ?? null));
      void qc.invalidateQueries({ queryKey: ['circuit-livrable', cid, vue.id] });
      setPlan(null);
      await qc.invalidateQueries({ queryKey: ['carte', cid] });
    } catch (e) {
      setErreur(e);
      if (!reessayable(e)) setPlan(null);
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <div className="encadre info pile" style={{ gap: 10 }} role="group" aria-label={`Publier vers ${cible}`}>
      <p>
        Publication vers {destination ?? cible}, par le collecteur de votre site : il relit l’outil avec le compte gardé chez
        vous, puis dépose le document validé, sans jamais rien supprimer. Rien n’est écrit avant votre geste.
      </p>
      {navigateur && <p className="encadre attention">{navigateur}</p>}
      {etat?.exige && !resultat && (
        <div className="pile" data-testid="circuit-publication" style={{ gap: 6 }}>
          {attestation ? (
            <p className="encadre ok">
              Le circuit de validation de votre entreprise a approuvé ce contenu et cette destination (
              <a href={`#/circuits/${etat.instance}`}>voir l’instance</a>) : la publication peut être exécutée, une seule fois.
            </p>
          ) : enCours ? (
            <p className="encadre attention" role="status">
              Soumis au circuit de validation : les décideurs sont prévenus et se prononcent dans Ekera (
              <a href={`#/circuits/${etat.instance}`}>suivre l’instance</a>). La publication s’ouvrira à la fin du circuit.
            </p>
          ) : (
            <>
              <p className="encadre info">
                {etat.soumis && !etat.sur_le_contenu_actuel
                  ? 'Le contenu a changé depuis la dernière soumission : il doit repasser le circuit de validation.'
                  : etat.soumis && etat.etat && etat.etat !== 'executee'
                    ? `Dernière soumission : ${etat.etat === 'refusee' ? 'refusée' : etat.etat === 'a_modifier' ? 'modification demandée' : etat.etat === 'caduque' ? 'caduque' : etat.etat === 'expiree' ? 'expirée' : 'retirée'}. Une nouvelle soumission ouvre une nouvelle instance.`
                    : 'Avant d’être publié, ce document passe le circuit de validation de votre entreprise (les étapes dépendent de son type et de sa destination).'}
              </p>
              <p>
                <button type="button" className="btn principal" disabled={soumission} onClick={() => void soumettre()}>
                  {soumission ? 'Soumission…' : 'Soumettre au circuit de validation'}
                </button>
              </p>
            </>
          )}
        </div>
      )}
      {aSoumettre && !resultat ? null : resultat ? (
        <p className="encadre ok" role="status">
          {resultat.deja_fait ? 'Déjà à jour dans ' : 'Publié dans '}
          {cible}
          {resultat.verifie ? ', relu après écriture.' : ' ; la relecture après écriture n’a pas confirmé le dépôt.'}
        </p>
      ) : plan ? (
        <>
          <p className="note">
            Plan calculé par le collecteur, gardé jusqu’à {new Date(plan.expire).toLocaleTimeString('fr-FR')} · empreinte{' '}
            <span className="num">{plan.empreinte.slice(0, 12)}</span>
          </p>
          <ul className="liste-simple" data-testid="plan-publication">
            {plan.plan.actions.map((a, i) => (
              <li key={i}>
                <b>{LIBELLE_ACTION[a.nature] ?? a.nature}</b> — {a.motif}
                {typeof a.donnees?.nom === 'string' ? ` (« ${a.donnees.nom} »)` : ''}
              </li>
            ))}
          </ul>
          <div className="ligne">
            <button type="button" className="btn principal" disabled={!!envoi || !!navigateur} onClick={() => void publier(plan)}>
              {envoi ? LIBELLE_ENVOI[envoi] : 'Valider ce plan par un geste et publier'}
            </button>
            <button type="button" className="btn" onClick={fermer} disabled={!!envoi}>
              Annuler
            </button>
          </div>
        </>
      ) : (
        <div className="ligne">
          <button type="button" className="btn principal" disabled={!!envoi} onClick={() => void demanderPlan()}>
            {envoi ? LIBELLE_ENVOI[envoi] : 'Demander le plan au collecteur'}
          </button>
          <button type="button" className="btn" onClick={fermer} disabled={!!envoi}>
            Annuler
          </button>
        </div>
      )}
      {erreur ? (
        <div className="encadre erreur" role="alert">
          <p>
            <b>Publication refusée.</b>
          </p>
          <p>{expliquerErreurDocuments(erreur)}</p>
        </div>
      ) : null}
    </div>
  );
}

const LIBELLE_ACTION: Record<string, string> = {
  deposer: 'Déposer le document',
  creer: 'Créer la page',
  nouvelle_version: 'Publier une nouvelle version',
  aucune: 'Rien à faire',
};

// ---------------------------------------------------------------- champs et sources

function Champ({ c, actif, surChoisir }: { c: ChampVue; actif: boolean; surChoisir: () => void }) {
  const ac = c.etat === 'a_completer';
  const nb = citationsDuChamp(c).length;
  const classes = ['doc-champ', ac ? 'a-completer' : '', actif ? 'actif' : ''].filter(Boolean).join(' ');
  return (
    <div
      id={`c-${c.id}`}
      className={classes}
      tabIndex={0}
      title={resumeSource(c)}
      onMouseEnter={surChoisir}
      onFocus={surChoisir}
      onClick={surChoisir}
      data-testid={`champ-${c.id}`}
    >
      <div className="doc-libelle">
        <span className={c.nature === 'texte' ? 'doc-consigne' : undefined}>{libelleAffiche(c)}</span>
        {ac ? (
          <span className="etiquette attention">À compléter</span>
        ) : (
          <span className="doc-renvoi" aria-label={resumeSource(c)}>
            {c.origine === 'moteur' ? 'moteur' : nb ? `${nb} source${nb > 1 ? 's' : ''}` : c.origine === 'livrable' ? 'Ekera' : ''}
          </span>
        )}
        {c.a_confirmer && <span className="etiquette neutre">à confirmer</span>}
      </div>
      <ValeurChamp c={c} />
    </div>
  );
}

/** Le libellé tel que le gabarit l'écrit ; pour un tableau (dont le libellé est la liste de ses en-têtes), son ampleur. */
function libelleAffiche(c: ChampVue): string {
  if (c.nature === 'liste' && c.entetes?.length) {
    const n = c.lignes?.length ?? 0;
    return c.etat === 'rempli' ? `Tableau du gabarit · ${n} ligne${n > 1 ? 's' : ''}` : 'Tableau du gabarit';
  }
  return c.libelle;
}

/** La valeur d'un champ, en texte brut (reprise par la vue d'une instance du circuit, CV3I-1). */
export function ValeurChamp({ c }: { c: ChampVue }) {
  if (c.etat === 'a_completer') return <p className="doc-valeur a-faire">{c.raison_libelle ?? c.detail ?? 'Aucune source ne règle ce champ.'}</p>;
  if (c.lignes?.length) {
    return (
      <div className="tableau-defile">
        <table className="doc-table">
          <thead>
            <tr>
              {(c.entetes ?? []).map((h, i) => (
                <th key={i} scope="col">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {c.lignes.map((l, i) => (
              <tr key={i} title={l.citations?.[0] ? `Source : ${l.citations[0].source}, ${localisateurAffiche(l.citations[0].localisateur)}` : undefined}>
                {l.cellules.map((x, j) => (
                  <td key={j} className={l.a_completer?.includes(j) ? 'a-faire' : undefined}>
                    {l.a_completer?.includes(j) ? 'à compléter' : x}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {c.ecartes > 0 && (
          <p className="note">
            {c.ecartes} élément{c.ecartes > 1 ? 's' : ''} écarté{c.ecartes > 1 ? 's' : ''} faute de citation vérifiée.
          </p>
        )}
      </div>
    );
  }
  if (c.phrases?.length)
    return (
      <div className="doc-valeur">
        {c.phrases.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
    );
  return <p className="doc-valeur">{c.valeur}</p>;
}

function MargeSources({ c }: { c: ChampVue | null }) {
  if (!c)
    return (
      <div className="pile" style={{ gap: 8 }}>
        <h3>Sources</h3>
        <p className="note">Survolez ou choisissez une valeur du document : sa source s’affiche ici, extrait recopié mot pour mot.</p>
      </div>
    );
  const cits = citationsDuChamp(c);
  return (
    <div className="pile" style={{ gap: 8 }} aria-live="polite">
      <h3>{c.libelle}</h3>
      {c.etat === 'a_completer' ? (
        <>
          <Etiquette ton="attention">À compléter</Etiquette>
          <p>{c.raison_libelle ?? 'Aucune source ne règle ce champ.'}</p>
          {c.detail && <p className="note">{c.detail}</p>}
        </>
      ) : (
        <>
          <span className="note">{LIBELLE_ORIGINE[c.origine ?? ''] ?? 'Origine inconnue'}</span>
          {c.calcul && (
            <p className="note">
              Calcul déterministe : <span className="num">{c.calcul.fonction}</span> → <span className="num">{c.calcul.champ}</span>
            </p>
          )}
          {c.a_confirmer && <p className="note">Cette valeur baisse un risque : elle reste à confirmer par une personne.</p>}
          {cits.slice(0, 8).map((x, i) => (
            <div className="citation" key={`${x.source}-${x.localisateur}-${i}`}>
              <q>{x.extrait}</q>
              <span className="src">
                {x.source} · {localisateurAffiche(x.localisateur)}
              </span>
            </div>
          ))}
          {cits.length > 8 && <p className="note">et {cits.length - 8} autre{cits.length - 8 > 1 ? 's' : ''} citation{cits.length - 8 > 1 ? 's' : ''}.</p>}
          {!cits.length && c.origine === 'carte' && <p className="note">Aucune citation.</p>}
        </>
      )}
    </div>
  );
}
